const R = 6371e3; // Earth's radius in meters

/** Haversine distance between two points in meters */
export function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Estimate time of arrival in minutes given distance (m) and speed (m/s) */
export function estimateETA(distanceMeters: number, speedMs: number | null): number | null {
  if (!speedMs || speedMs < 0.5) return null; // Too slow or stationary
  return Math.round(distanceMeters / speedMs / 60);
}

/** Find the nearest stop to a position, returns the stop index */
export function findNearestStopIndex(
  lat: number,
  lng: number,
  stops: { latitude: number; longitude: number }[]
): number {
  let minDist = Infinity;
  let minIdx = 0;
  for (let i = 0; i < stops.length; i++) {
    const d = haversineDistance(lat, lng, stops[i].latitude, stops[i].longitude);
    if (d < minDist) {
      minDist = d;
      minIdx = i;
    }
  }
  return minIdx;
}

/** Count stops remaining from bus position to a target stop */
export function stopsAway(
  busLat: number,
  busLng: number,
  targetStopIndex: number,
  stops: { latitude: number; longitude: number }[]
): number {
  const busNearestIdx = findNearestStopIndex(busLat, busLng, stops);
  const idxDiff = targetStopIndex - busNearestIdx;

  // If index says arrived (0 or past), verify bus is actually near the target stop
  if (idxDiff <= 0) {
    const distToTarget = haversineDistance(
      busLat, busLng,
      stops[targetStopIndex].latitude, stops[targetStopIndex].longitude
    );
    // Only "arrived" if within 500m of the target stop
    if (distToTarget > 500) {
      // Bus is far away — return remaining stops based on distance to nearest stop
      return Math.max(1, stops.length - busNearestIdx + targetStopIndex);
    }
    return 0;
  }

  return idxDiff;
}

/** Format distance for display */
export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
}
